import { useEffect } from 'react';
import { useLocalStorage } from './useLocalStorage';

/**
 * useAppState — Centralized application persistent state.
 *
 * All state keys are namespaced under `bursaku_` in localStorage so they
 * don't collide with other apps or libraries.
 *
 * Current keys:
 *   bursaku_theme            — 'light' | 'dark'
 *   bursaku_devtoolbar_open  — boolean
 *   bursaku_navbar_collapsed — boolean  (main app sidebar state)
 *   bursaku_default_page_size — number  (default rows per page in tables)
 *   bursaku_font_scale       — number  (font size scaling factor)
 *   bursaku_current_user     — string   (logged-in username, demo session)
 *
 * Returns a flat object so consumers can destructure what they need.
 */
export function useAppState() {
  // ── Theme ────────────────────────────────────────────────────────
  const [theme, setTheme] = useLocalStorage('bursaku_theme', 'light');

  // Apply / remove the dark class on <html> whenever theme changes
  useEffect(() => {
    const html = document.documentElement;
    if (theme === 'dark') {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () =>
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));

  // ── DevToolbar ───────────────────────────────────────────────────
  const [devToolbarOpen, setDevToolbarOpen] = useLocalStorage(
    'bursaku_devtoolbar_open',
    false
  );
  const toggleDevToolbar = () => setDevToolbarOpen((prev) => !prev);

  // ── AppShell sidebar ─────────────────────────────────────────────
  const [navbarCollapsed, setNavbarCollapsed] = useLocalStorage(
    'bursaku_navbar_collapsed',
    false
  );
  const toggleNavbarCollapsed = () => setNavbarCollapsed((prev) => !prev);

  // ── Tables ───────────────────────────────────────────────────────
  // Default number of rows per page used by list pages on load.
  const [defaultPageSize, setDefaultPageSize] = useLocalStorage(
    'bursaku_default_page_size',
    10
  );

  // ── Font Scale ────────────────────────────────────────────────────
  const [fontScale, setFontScale] = useLocalStorage('bursaku_font_scale', 1);
  useEffect(() => {
    document.documentElement.style.setProperty('--font-scale', String(fontScale));
  }, [fontScale]);

  // ── Demo session ──────────────────────────────────────────────────
  // Resolved to a real user via settingsSelectors.resolveCurrentUser().
  const [currentUserUsername, setCurrentUserUsername] = useLocalStorage(
    'bursaku_current_user',
    'developer'
  );

  return {
    // Theme
    theme,
    setTheme,
    toggleTheme,

    // DevToolbar
    devToolbarOpen,
    setDevToolbarOpen,
    toggleDevToolbar,

    // AppShell sidebar
    navbarCollapsed,
    setNavbarCollapsed,
    toggleNavbarCollapsed,

    // Tables
    defaultPageSize,
    setDefaultPageSize,

    // Font Scale
    fontScale,
    setFontScale,

    // Session
    currentUserUsername,
    setCurrentUserUsername,
  };
}
